import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import useDownloadLogs from './hooks/useDownloadLogs';
import messages from '../../lib/i18n';
import '../../styles/timeline.css';
import MessagePlaceholder from '../Parts/MessagePlaceholder';
import config from '../../config';
import { convertISOFormatToDate } from '../../actions/actionLog';
import { ACTION_TYPES, mediaType } from '../../lib/constants';
import thumbnailVideo from '../../images/media/img_group_01.svg';
import thumbnailAudio from '../../images/media/img_group_02.svg';
import thumbnailVideoDelete from '../../images/media/img_group_01_delete.svg';
import thumbnailAudioDelete from '../../images/media/img_group_02_delete.svg';
import thumbnailPhotoDelete from '../../images/media/img_group_03_delete.svg';
import {
  Lists,
  HeaderTableWrapper,
  Table,
  Thead,
  HeaderTr,
  ThColumn,
  TableWrapper,
  Tbody,
  BodyTr,
  TdColumn,
  ALink,
  MediaIcon,
  StyledBodyTr,
  StyledRowDiv,
  StyledImageDiv,
  StyledItemDiv,
  StyledTextDiv,
  StyledP,
  smallPlayCircleStyle,
  largePlayCircleStyle,
  MediaContainer,
} from './styles/ActionLogListStyles';
import { ReactComponent as PlayCircle } from '../../images/common/play_circle.svg';

export const columnWidths = {
  location: '15%',
  operatorName: '15%',
  groupName: '15%',
  deviceName: '15%',
  date: '15%',
  action: '15%',
  data: '10%',
};

const { googleMapsApi } = config;

const blankSymbol = '-';
const { locale } = config;
const i18nMessages = messages[locale];

/**
 * ログ一覧の生成
 * @param {*} log ログ情報
 * @param {*} onUpdateMedia メディアURL更新
 * @param {*} displayDownloadLogs ダウンロードデータ表示
 * @returns ログ一覧
 */
const createList = (log, groups, onUpdateMedia, displayDownloadLogs) => {
  const groupName = (
    groups.find(group => group.id === log.groupId) || { name: '' }
  ).name;
  const actions = i18nMessages['actionLog.list.actions'];
  const date = convertISOFormatToDate(log.occurredAt);
  const ipAddress = (log.location && log.location.ipAddress) || null;
  const latitude = (log.location && log.location.latitude) || null;
  const longitude = (log.location && log.location.longitude) || null;
  const workRecord = log.workRecord || { mediaType: mediaType.none };
  const thumbnail =
    (workRecord.mediaType === mediaType.image && workRecord.thumbnailUrl) ||
    (workRecord.mediaType === mediaType.video &&
      (workRecord?.thumbnailUrl || thumbnailVideo)) ||
    (workRecord.mediaType === mediaType.audio && thumbnailAudio);
  const deletedThumbnail =
    (workRecord.mediaType === mediaType.image && thumbnailPhotoDelete) ||
    (workRecord.mediaType === mediaType.video && thumbnailVideoDelete) ||
    (workRecord.mediaType === mediaType.audio && thumbnailAudioDelete);

  return (
    <BodyTr key={log.id} hasDownloadData={!!displayDownloadLogs(log.id)}>
      <TdColumn width={columnWidths.date}>{date}</TdColumn>
      <TdColumn width={columnWidths.action}>{actions[log.action]}</TdColumn>
      <TdColumn width={columnWidths.operatorName}>{log.operatorName}</TdColumn>
      <TdColumn width={columnWidths.groupName}>
        {groupName || blankSymbol}
      </TdColumn>
      <TdColumn width={columnWidths.deviceName}>
        {log.deviceName || blankSymbol}
      </TdColumn>
      <TdColumn width={columnWidths.location}>
        {!log.location && blankSymbol}
        {log.location && latitude && longitude && (
          <ALink href={googleMapsApi(latitude, longitude)} target="_blank">
            <FormattedMessage id="actionLog.list.map" />
          </ALink>
        )}
        {log.location && latitude && longitude && ipAddress && (
          <FormattedMessage id="actionLog.list.location.delimiter" />
        )}
        {log.location && ipAddress}
      </TdColumn>
      <TdColumn width={columnWidths.data}>
        {log.action === ACTION_TYPES.photograph ||
        log.action === ACTION_TYPES.movie_recording ||
        log.action === ACTION_TYPES.voice_recording ? (
          <MediaContainer>
            <MediaIcon
              alt="IMG"
              src={workRecord.isDeleted ? deletedThumbnail : thumbnail}
              onClick={() =>
                onUpdateMedia({
                  type: workRecord.mediaType,
                  url: workRecord.mediaFileUrl,
                })
              }
            />
            {workRecord.mediaType === mediaType.video &&
              workRecord.thumbnailUrl && (
                <PlayCircle style={smallPlayCircleStyle} />
              )}
          </MediaContainer>
        ) : (
          blankSymbol
        )}
      </TdColumn>
    </BodyTr>
  );
};

/**
 * ヘッダーの生成
 * @returns ヘッダー
 */
const createHeader = () => (
  <HeaderTr>
    <ThColumn width={columnWidths.date}>
      <FormattedMessage id="actionLog.list.header.date" />
    </ThColumn>
    <ThColumn width={columnWidths.action}>
      <FormattedMessage id="actionLog.list.header.action" />
    </ThColumn>
    <ThColumn width={columnWidths.operatorName}>
      <FormattedMessage id="actionLog.list.header.operator" />
    </ThColumn>
    <ThColumn width={columnWidths.groupName}>
      <FormattedMessage id="actionLog.list.header.group" />
    </ThColumn>
    <ThColumn width={columnWidths.deviceName}>
      <FormattedMessage id="actionLog.list.header.device" />
    </ThColumn>
    <ThColumn width={columnWidths.location}>
      <FormattedMessage id="actionLog.list.header.location" />
    </ThColumn>
    <ThColumn width={columnWidths.data}>
      <FormattedMessage id="actionLog.list.header.data" />
    </ThColumn>
  </HeaderTr>
);

const ActionLogLists = ({ logs, groups, onUpdateMedia }) => {
  const downloadLogs = useDownloadLogs(logs);
  const displayDownloadLogs = logId =>
    downloadLogs.find(dl => dl?.logId === logId)?.downloadLog;

  return (
    <Lists>
      <HeaderTableWrapper>
        <Table>
          <Thead>{createHeader()}</Thead>
        </Table>
      </HeaderTableWrapper>
      <TableWrapper>
        <Table>
          {(logs && logs.length > 0 && (
            <Tbody>
              {logs.map(log => (
                <>
                  {createList(log, groups, onUpdateMedia, displayDownloadLogs)}
                  {/* ダウンロードデータ表示 */}
                  {displayDownloadLogs(log.id) && (
                    <StyledBodyTr key={`expanded-${log.id}`}>
                      <TdColumn colSpan={100}>
                        <StyledRowDiv>
                          {displayDownloadLogs(log.id).map(dl => (
                            <StyledItemDiv>
                              <StyledImageDiv
                                onClick={() =>
                                  onUpdateMedia({
                                    type: dl.mediaType,
                                    url: dl.mediaFileUrl,
                                  })
                                }
                              >
                                <MediaIcon
                                  alt="IMG"
                                  src={dl.thumbnailUrl}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                  }}
                                />
                                {dl.mediaType === mediaType.video &&
                                  dl.thumbnailUrl && (
                                    <PlayCircle style={largePlayCircleStyle} />
                                  )}
                              </StyledImageDiv>
                              <StyledTextDiv>
                                <StyledP>
                                  {convertISOFormatToDate(dl.recordedDate)}
                                </StyledP>
                                <StyledP>{dl.operatorName}</StyledP>
                              </StyledTextDiv>
                            </StyledItemDiv>
                          ))}
                        </StyledRowDiv>
                      </TdColumn>
                    </StyledBodyTr>
                  )}
                </>
              ))}
            </Tbody>
          )) || (
            <tbody>
              <tr>
                <td>
                  <MessagePlaceholder />
                </td>
              </tr>
            </tbody>
          )}
        </Table>
      </TableWrapper>
    </Lists>
  );
};
ActionLogLists.propTypes = {
  logs: PropTypes.array.isRequired,
  groups: PropTypes.array.isRequired,
  onUpdateMedia: PropTypes.func.isRequired,
};

export default ActionLogLists;
